import axios, { AxiosInstance } from 'axios';
import { apiConfig } from '@/config/apiConfig';
import { fmtCount, getVoiceFallbackClient } from './_shared';

// ── Voice overview (/voice) ─────────────────────────────────────────

export type VoiceOverviewKpi = { 
  label: string; 
  val: string; 
  delta: string; 
  series?: number[]; 
};

export type VoiceOverviewCampaign = {
  icon: string;
  name: string;
  sub: string;
  path: string;
  status?: string;
};

export type VoiceOverviewPayload = {
  kpis: VoiceOverviewKpi[];
  campaigns: VoiceOverviewCampaign[];
  /** Daily bar heights for the session activity chart */
  sessionActivity: number[];
  sessionDates?: string[];
  sessionChartTitle: string;
};


/** Map arbitrary `/api/analytics/overview` JSON into the dashboard payload shape. */
export function normalizeVoiceOverview(data: unknown): VoiceOverviewPayload {
  const d = data as any;
  if (d && Array.isArray(d.kpis) && Array.isArray(d.campaigns) && Array.isArray(d.sessionActivity)) {
    return d as VoiceOverviewPayload;
  }
  
  const base: VoiceOverviewPayload = {
    kpis: [
      { label: 'Voice sessions', val: '—', delta: '—' },
      { label: 'Appointments booked', val: '—', delta: '—' },
      { label: 'Cost / booking', val: '—', delta: '—' },
      { label: 'Success fee revenue', val: '—', delta: '—' },
    ],
    campaigns: [],
    sessionActivity: [],
    sessionChartTitle: 'Session activity',
  };

  if (!d || typeof d !== 'object') return base;

  const sessions = d.total_sessions ?? d.voice_sessions ?? d.sessions ?? d.session_count;
  const bookings = d.appointments_booked ?? d.bookings ?? d.appointment_count;
  const cpa =
    d.cost_per_booking_cents != null
      ? `$${(Number(d.cost_per_booking_cents) / 100).toFixed(2)}`
      : d.cost_per_booking ?? d.cost_per_appt;
  const revenue =
    d.success_fee_revenue_cents != null
      ? `$${(Number(d.success_fee_revenue_cents) / 100).toLocaleString()}`
      : d.success_fee_revenue ?? d.revenue;

  if (sessions != null) base.kpis[0] = { ...base.kpis[0], val: fmtCount(sessions) };
  if (bookings != null) base.kpis[1] = { ...base.kpis[1], val: fmtCount(bookings) };
  if (cpa != null) base.kpis[2] = { ...base.kpis[2], val: String(cpa) };
  if (revenue != null) base.kpis[3] = { ...base.kpis[3], val: String(revenue) };

  const campSrc = d.featured_campaigns ?? d.top_campaigns ?? d.campaign_cards ?? d.campaigns;
  if (Array.isArray(campSrc) && campSrc.length) {
    base.campaigns = campSrc.map((c: any, i: number) => ({
      icon: 'solar:home-2-bold',
      name: String(c.name ?? c.title ?? `Campaign ${i + 1}`),
      sub: String(c.subtitle ?? c.sub ?? `${fmtCount(c.sessions ?? c.session_count)} sessions`),
      path: '/voice-campaigns',
      status: String(c.status ?? 'Unknown'),
    }));
  }

  if (Array.isArray(d.session_activity)) {
    base.sessionActivity = d.session_activity.map((x: any) => Number(x) || 0);
  }
  if (Array.isArray(d.session_dates)) {
    base.sessionDates = d.session_dates.map((x: any) => String(x));
  }
  if (typeof d.session_chart_title === 'string') base.sessionChartTitle = d.session_chart_title;

  return base;
}

export async function fetchVoiceOverview(axiosInstance?: AxiosInstance): Promise<VoiceOverviewPayload> {
  const client = axiosInstance || getVoiceFallbackClient();
  const overviewUrl = `${apiConfig.voiceEndpoints.analytics}/overview`;
  const campaignsUrl = `${apiConfig.voiceEndpoints.analytics}/campaigns`;
  const trendsUrl = `${apiConfig.voiceEndpoints.analytics}/trends`;
  
  const [overviewRes, campaignsRes, trendsRes] = await Promise.all([
    client.get<unknown>(overviewUrl, { params: { days: 30 } }).catch(() => ({ data: null })),
    client.get<any>(campaignsUrl, { params: { days: 30 } }).catch(() => ({ data: [] })),
    client.get<any>(trendsUrl, { params: { days: 30 } }).catch(() => ({ data: null }))
  ]);
  
  const overviewData = (overviewRes.data as any)?.data || overviewRes.data;
  const payload = normalizeVoiceOverview(overviewData);

  const campaignsList = campaignsRes.data?.data || campaignsRes.data?.campaigns || campaignsRes.data;
  if (Array.isArray(campaignsList) && campaignsList.length > 0) {
    payload.campaigns = campaignsList.map((c: any, i: number) => ({
      icon: 'solar:home-2-bold',
      name: String(c.name ?? c.title ?? `Campaign ${i + 1}`),
      sub: String(c.subtitle ?? c.sub ?? `${fmtCount(c.sessions ?? c.session_count ?? c.volume ?? 0)} sessions`),
      path: '/voice-campaigns',
      status: String(c.status ?? 'Unknown'),
    }));
  }

  const td = trendsRes.data?.data || trendsRes.data?.trends || trendsRes.data;
  if (td && typeof td === 'object') {
    const sessionAct = td.session_activity ?? td.sessionActivity ?? td.activity;
    if (Array.isArray(sessionAct)) {
      payload.sessionActivity = sessionAct.map((x: any) => Number(x) || 0);
    }
    const sessionDates = td.session_dates ?? td.sessionDates ?? td.dates;
    if (Array.isArray(sessionDates)) {
      payload.sessionDates = sessionDates.map((x: any) => String(x));
    }
    const chartTitle = td.session_chart_title ?? td.sessionChartTitle ?? td.title;
    if (typeof chartTitle === 'string') {
      payload.sessionChartTitle = chartTitle;
    }
  }

  return payload;
}
